import React, { useCallback, useMemo, useRef, useState } from "react";
import { DropdownChevronDown } from "@vibe/icons";
import { Button, LinearProgressBar, Text } from "@vibe/core";
import ItemNameComponent from "./ItemNameComponent";
import DiscrepancyGrid from "./DiscrepancyGrid";
import DiscrepancyListSkeleton from "./DiscrepancyListSkeleton";
import MondayValueCell from "./DiscrepancyGrid/MondayValueCell";
import DisplayValueComponent from "./DisplayValueComponent";
import { ALARM_CHIP, CLEAN_CHIP, OPEN_ROW_GROUND, QUIET_CHIP } from "./discrepancyChips";
import SubitemResyncModal, { SubitemResyncRequest } from "./SubitemResyncModal";
import { patchSubitemScanAfterResync } from "./subitemOptimisticUpdate";
import SubitemDestructiveModal, { SubitemDestructiveRequest } from "./SubitemDestructiveModal";
import useSubitemDiscrepancy from "../../hooks/api/useSubitemDiscrepancy";
import { toSubitemBoardHeaders, toSubitemRows, SubitemRowActions } from "./subitemScanAdapter";
import type { SubitemScanParent, SubitemScanSubitem } from "./__fixtures__/discrepancyFixtures";
import type { BoardHeader } from "./DiscrepancyGrid/discrepancyGrid.interface";
import type { SubitemDiscrepancyWalk } from "../../hooks/api/useSubitemDiscrepancyWalk";

interface SyncedItem {
  _id: string;
  item_name?: string;
}

interface SubitemsTableProps {
  syncedItems: SyncedItem[];
  currentBoard: number | null;
  token: string | null;
  filterQuery: string;
  itemsLoading: boolean;
  /**
   * The page window. In All mode it is applied exactly as ItemsTable applies it — this list
   * pages, it does not virtualize; see the slice below for which fetch mode actually applies it
   * and why. In Discrepancies mode it is applied to the walk's drifting set instead.
   */
  startIndex: number;
  limit: number;
  /**
   * The ITEMS tab's filter, which is what `useBoardView` switches its fetching strategy on —
   * NOT this tab's own `filterQuery`. The two are separate settings and only this one says
   * whether `syncedItems` arrived as a single server page or as the whole accumulated set.
   *
   * Passed rather than inferred because this tab cannot tell the two apart by looking: 25
   * parents is 25 parents whether it is page one of nine or the entire board.
   *
   * Read in All mode only. In Discrepancies mode this tab pages the walk, not `syncedItems`.
   */
  fetchFilter: string;
  /**
   * The board-wide walk behind this tab's Discrepancies filter, owned by the page — see
   * useSubitemDiscrepancyWalk for why it is not owned here. Read only while `filterQuery` is
   * Discrepancies; in All mode this tab scans the page it was handed, as it always has.
   */
  walk: SubitemDiscrepancyWalk;
}

const DISCREPANCIES_FILTER = "discrepancies";

/**
 * Looks up which parent a subitem belongs to, plus that parent's board list. The adapter's
 * row-level actions (`onResyncColumn`/`onResyncName`) only ever hand back `subitemSyncId`
 * (and, for columns, `columnKey`) — never the parent or the row data itself — so this is
 * how the handlers below recover enough context to build a resync request.
 */
const findSubitemContext = (
  parents: SubitemScanParent[],
  subitemSyncId: string,
): { parentSyncId: string; subitem: SubitemScanSubitem; boards: BoardHeader[] } | undefined => {
  for (const parent of parents) {
    const subitem = parent.subitems.find((candidate) => candidate.subitemSyncId === subitemSyncId);
    if (subitem) {
      return { parentSyncId: parent.parentSyncId, subitem, boards: toSubitemBoardHeaders(parent) };
    }
  }
  return undefined;
};

/*
 * The virtualization threshold and scroll-box height this tab used to declare for itself
 * (48 and 720) now live in DiscrepancyGrid as its defaults, so the Shared Items tab gets
 * them too — see VIRTUALIZE_THRESHOLD there for why those numbers.
 *
 * A number each call site passes for itself is uniform only until one of them forgets: these
 * two tabs render the SAME grid one click apart and were windowing at 48 rows here and 100
 * there, in boxes of different heights. Nothing below passes either value now; the props
 * remain on the grid for a caller with a genuinely different container.
 */
interface ParentRowProps {
  syncItem: SyncedItem;
  parent: SubitemScanParent | undefined;
  isOpen: boolean;
  onToggle: () => void;
  gridFilter: "all" | "discrepancies";
  /**
   * Optional, and normally omitted so the grid's own default applies — see the note above on
   * why this stopped being a number this component decides. Passed straight through, so
   * `undefined` here means "the shared default", not "no height".
   */
  virtualizedHeight?: number;
  actions: SubitemRowActions;
  // Scans this one deferred/unverified parent on demand — see useSubitemDiscrepancy's
  // `scanParent`. Exported so /discrepancy-preview (DiscrepancyPreview.tsx) can render the same
  // ParentRow the production tab does for every parent status, rather than reinventing this
  // component's branches against raw fixtures.
  onScanParent: (parentSyncId: string) => Promise<unknown>;
}

export const ParentRow: React.FC<ParentRowProps> = ({
  syncItem,
  parent,
  isOpen,
  onToggle,
  gridFilter,
  virtualizedHeight,
  actions,
  onScanParent,
}) => {
  // Both are live Monday names of the mirror on the viewing board (the database's
  // setup-time `itemname` no longer reaches either). The page list's is preferred
  // because a `deferred` parent is the one case the scan does not read from Monday —
  // its `parentName` is the snapshot until the parent is expanded — while the page
  // list is live for every row. In Discrepancies mode there is no page list, so the
  // scan's name stands.
  const displayName = syncItem.item_name || parent?.parentName || "";

  // "idle" | "scanning" | "error". Deliberately no "scanned" member here: once the scan
  // succeeds, the merged parent's own `status` flips from "deferred" to "scanned" (the hook's
  // job, not this component's), so this whole branch stops rendering on the very next render —
  // there is nothing for a fourth state to track. That data-driven transition is also what
  // makes a second expand a no-op rather than a re-scan: there is no "Scan this item" button
  // left to click once `parent.status !== "deferred"`.
  const [scanStatus, setScanStatus] = useState<"idle" | "scanning" | "error">("idle");
  const handleScan = async () => {
    if (!parent) return;
    setScanStatus("scanning");
    try {
      await onScanParent(parent.parentSyncId);
      setScanStatus("idle");
    } catch {
      setScanStatus("error");
    }
  };

  // Deliberately NOT gated on `isOpen`: whether the grid is even mounted is a separate decision
  // (below, in the JSX), and mixing the two here would make that decision untestable — resetting
  // this to `[]` on every collapse would make a `display: none` mutation of the mount condition
  // LOOK like it still unmounted, since the (still-mounted) grid would have nothing to show
  // either way. `parent` and `actions` are genuine dependencies, not omitted — Task 1 removed
  // exactly that omission from Shared Items (SharedColumns.tsx:86-99), and it must not come back
  // here.
  const boards = useMemo(() => (parent ? toSubitemBoardHeaders(parent) : []), [parent]);
  const rows = useMemo(() => (parent ? toSubitemRows(parent, actions) : []), [parent, actions]);
  /*
   * Which subitem groups are shut. Subitems with nothing wrong start collapsed and everything
   * drifting starts open, so a parent opens onto its problems rather than onto fifty rows of
   * agreeing columns. The band carries each subitem's state either way, so a collapsed group
   * still reports what is wrong with it.
   *
   * Derived from the scan on first read and then owned locally, so a revalidation that returns
   * the same verdicts does not reopen groups the reader deliberately shut. `undefined` means
   * "not derived yet" — distinct from an empty set, which legitimately means "everything open".
   */
  const [collapsedOverride, setCollapsedOverride] = useState<Set<string> | undefined>(undefined);

  const defaultCollapsed = useMemo(
    () =>
      new Set(
        (parent?.subitems ?? [])
          .filter((subitem) => subitem.state === "ok")
          .map((subitem) => subitem.subitemSyncId),
      ),
    [parent],
  );

  const collapsedGroups = collapsedOverride ?? defaultCollapsed;

  const handleToggleGroup = useCallback(
    (groupId: string) => {
      setCollapsedOverride((previous) => {
        const next = new Set(previous ?? defaultCollapsed);
        if (next.has(groupId)) next.delete(groupId);
        else next.add(groupId);
        return next;
      });
    },
    [defaultCollapsed],
  );

  return (
    <div>
      <div style={{ overflow: "hidden", background: "var(--primary-background-color)" }}>
        {/* The row itself. No card, no gutter, no radius: the list wrapper owns the border and
            neighbours share one hairline, so synced items stack the way a Monday board stacks
            its own rows rather than as five separate panels. */}
        <div
          onClick={onToggle}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onToggle();
            }
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--spacing-small)",
            padding: "var(--spacing-small) var(--spacing-medium)",
            minHeight: 40,
            // Open rows take a tint mixed a step deeper than --allgrey, because the subitem
            // group bands INSIDE an open row already own --allgrey. Same grey at both depths
            // reads as one depth. Mixed from tokens rather than hardcoded so it still follows
            // a theme switch.
            background: isOpen ? OPEN_ROW_GROUND : "var(--primary-background-color)",
            borderBottom: isOpen ? "1px solid var(--layout-border-color)" : "none",
            cursor: "pointer",
          }}
        >
          {/* Leads the row rather than sitting at the far right edge: on a row this wide the
              toggle was a long way from the name it opens. */}
          <span
            style={{
              display: "inline-flex",
              flex: "none",
              color: "var(--secondary-text-color)",
              transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.15s ease",
            }}
          >
            <DropdownChevronDown size={20} />
          </span>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "var(--spacing-small)",
              alignItems: "center",
              flexWrap: "wrap",
              minWidth: 0,
            }}
          >
            <ItemNameComponent itemName={displayName} />
            {parent?.status === "scanned" && parent.totalDiscrepancies > 0 ? (
              // Two Counters rather than the old text-Counter-text sandwich. Counter can only
              // render `prefix + count`, so the previous version put a red circle mid-sentence
              // between two loose text nodes; as chips the pair reads as one unit at list speed.
              <span
                data-testid={`subitems-table-badge-${syncItem._id}`}
                style={{ display: "inline-flex", gap: "var(--spacing-xs)", alignItems: "center" }}
              >
                <span style={QUIET_CHIP}>{`${parent.subitemsAffected} subitems`}</span>
                <span style={ALARM_CHIP}>{`${parent.totalDiscrepancies} issues`}</span>
              </span>
            ) : parent?.status === "scanned" ? (
              // A clean parent used to render NOTHING here, which made "we checked and this is
              // fine" look identical to "this hasn't loaded yet" — the two most different
              // outcomes in the list, indistinguishable in the default view.
              <span style={CLEAN_CHIP} data-testid={`subitems-table-clean-${syncItem._id}`}>
                In sync
              </span>
            ) : parent?.status === "deferred" ? (
              <Text type="text2" color="secondary">
                Not yet scanned
              </Text>
            ) : parent?.status === "unverified" ? (
              <Text type="text2" color="secondary">
                Scan incomplete
              </Text>
            ) : null}
          </div>
        </div>

        {isOpen ? (
          !parent ? (
            <div style={{ padding: "var(--spacing-large)" }}>
              <Text type="text2" color="secondary">
                Loading...
              </Text>
            </div>
          ) : parent.status === "scanned" ? (
            <>
              <DiscrepancyGrid
                collapsedGroups={collapsedGroups}
                onToggleGroup={handleToggleGroup}
                boards={boards}
                rows={rows}
                filter={gridFilter}
                virtualizedHeight={virtualizedHeight}
                emptyMessage="No discrepancies in this item's subitems."
                // Without this the grid falls back to renderDefaultValue — plain text for
                // every column type — which is what made this tab and Shared Items disagree
                // about how a value looks. Both meta fields are already threaded by
                // subitemScanAdapter; neither needs a new request.
                renderValue={(value, row) => {
                  const columnType = (row.meta?.columnType as string) ?? null;

                  // board_relation goes to DisplayValueComponent, exactly as Shared Items
                  // routes it: resolving linked item NAMES needs an async Monday lookup, and
                  // MondayValueCell is pure and synchronous by design. Sending it here would
                  // render nothing at all — the cell's `value`/`text` are both empty for this
                  // type — which is the blank-row bug this routing exists to fix. Keeping both
                  // tabs on the same component is also the point of the Phase 2 unification.
                  if (columnType === "board_relation") {
                    return (
                      <DisplayValueComponent
                        columnExists={value?.exists ?? false}
                        columnValue={value?.value ?? null}
                        columnText={value?.text ?? null}
                        columnType={columnType}
                        linkedItemIds={value?.linkedItemIds ?? null}
                      />
                    );
                  }

                  return (
                    <MondayValueCell
                      value={value}
                      columnType={columnType}
                      dropdownLabels={
                        (value as { dropdownLabels?: string[] } | null)?.dropdownLabels
                      }
                    />
                  );
                }}
              />
            </>
          ) : parent.status === "deferred" ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--spacing-medium)",
                padding: "var(--spacing-large)",
              }}
            >
              <Text type="text2" color="secondary">
                This item's subitems haven&apos;t been scanned yet. Scanning every synced item's
                subitems up front would be far too slow, so it happens on demand.
              </Text>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--spacing-small)",
                  alignItems: "flex-start",
                }}
              >
                {scanStatus === "error" ? (
                  <Text type="text2" color="negative">
                    The scan failed. Please try again.
                  </Text>
                ) : null}
                <Button size="small" onClick={handleScan} loading={scanStatus === "scanning"}>
                  Scan this item
                </Button>
              </div>
            </div>
          ) : (
            // status === "unverified". No grid, no verdict badges, no actions: the engine could
            // not reach every board this parent spans, so no absence-based verdict (missing /
            // orphaned_record, both of which carry destructive remedies) can be trusted here.
            // Showing this parent through the same branch as a clean scan is exactly the failure
            // mode this branch exists to prevent.
            <div style={{ padding: "var(--spacing-large)" }}>
              <Text type="text2" color="secondary">
                This item couldn&apos;t be fully scanned — some of its boards were unreachable, so
                its subitem results aren&apos;t shown. Treating a partial scan as a clean result
                could hide a real discrepancy or wrongly flag a subitem as missing.
              </Text>
            </div>
          )
        ) : null}
      </div>
    </div>
  );
};

const WALK_PROGRESS_LABEL = "Scanning subitems for discrepancies.";

const countOf = (count: number, noun: string) => `${count} ${count === 1 ? noun : `${noun}s`}`;

/**
 * A failed single-parent rescan after a write leaves that row on its previous verdict; the
 * write itself already reported its own outcome, and there is nothing further to do here.
 */
const swallow = () => undefined;

/**
 * The parents the walk could not verdict on, named because they are absent from the list.
 * Copy says where they CAN be found — the All Items view labels each of them ("Not yet
 * scanned" with a Scan button for a deferred parent; "Scan incomplete" for an unverified one).
 */
const SkippedNote: React.FC<{ count: number }> = ({ count }) => (
  <Text type="text2" color="secondary" data-testid="subitem-walk-skipped">
    {`${countOf(count, "item")} couldn't be scanned automatically and ${
      count === 1 ? "isn't" : "aren't"
    } listed here. Switch to All Items to see ${count === 1 ? "it" : "them"}.`}
  </Text>
);

/**
 * The walk's own progress line, in the Items tab's shape (percentage, label, "x of y items") so
 * the two tabs' Discrepancies modes read as the same feature. Rendered only while windows are
 * still in flight: a finished walk has the rows to speak for it, and the skipped note takes
 * over once it is done.
 */
const WalkProgress: React.FC<{ walk: SubitemDiscrepancyWalk }> = ({ walk }) => {
  if (walk.status === "scanning") {
    const percentage = walk.totalCount
      ? Math.min(Math.round((walk.scannedCount / walk.totalCount) * 100), 100)
      : 0;
    return (
      <div className="mb-4" data-testid="subitem-walk-progress">
        <LinearProgressBar value={percentage} />
        <div className="flex justify-between mt-1">
          <Text type="text2" color="secondary">
            {`${percentage}% ${WALK_PROGRESS_LABEL}`}
          </Text>
          <Text type="text2" color="secondary">
            {`${walk.scannedCount} of ${walk.totalCount} items`}
          </Text>
        </div>
      </div>
    );
  }
  if (walk.status === "done" && walk.skippedCount > 0) {
    return (
      <div className="mb-4">
        <SkippedNote count={walk.skippedCount} />
      </div>
    );
  }
  return null;
};

const SubitemsTable: React.FC<SubitemsTableProps> = ({
  syncedItems,
  currentBoard,
  token,
  filterQuery,
  itemsLoading,
  startIndex,
  limit,
  fetchFilter,
  walk,
}) => {
  const [openParents, setOpenParents] = useState<Record<string, boolean>>({});

  // This tab's Discrepancies filter is answered by the board-wide walk, never by filtering the
  // page in hand — see `pageItems` below for the history.
  const isWalkMode = filterQuery === DISCREPANCIES_FILTER;

  /*
   * The parents THIS PAGE shows — and, deliberately, the only ones scanned.
   *
   * This used to be `syncedItems.map(...)`: every synced item the page had in hand, sent to one
   * synchronous POST. The `.slice()` further down trimmed what was RENDERED, but the scan had
   * already asked for all of it, and the two modes fail differently:
   *
   *   All mode           `syncedItems` is one server page, so this was `limit` parents — 25 since
   *                      edba16a raised it from 8, against SUBITEM_BUDGET_PER_PARENT of 500. Up
   *                      to ~12,500 subitems in a single request.
   *   Discrepancies      `useBoardView` streams and ACCUMULATES the whole board into
   *                      `syncedItems`, so this was unbounded — hundreds of parents. Worse, the
   *                      SWR key is the joined id list, so every accumulation batch minted a new
   *                      key and re-scanned a larger set from scratch.
   *
   * simb-backend caps the proxy at SUBITEM_DISCREPANCY_TIMEOUT_MS (30s), so past a certain board
   * size this could only ever 504 — which is the "scan failed" the Subitems tab reports.
   *
   * Windowing here instead bounds every scan to `limit` parents and gives each page a STABLE SWR
   * key, so paging back and forth hits the cache rather than re-scanning.
   *
   * The window is taken BEFORE the drift filter below, and it has to be: in Discrepancies mode
   * that filter can only tell whether a parent belongs once its scan is in, so filtering first
   * would need the very scan this list defines.
   */
  const pageSyncItems = useMemo(() => {
    // Nothing to scan per page in Discrepancies mode: the walk has, or is getting, a verdict for
    // every parent on the board, and scanning this page's parents again would only repeat it.
    if (isWalkMode) return [];
    // Same asymmetry as the old `pageItems` and as ItemsTable's: in All mode the server already
    // returned one page, so slicing again with the same startIndex would cut page two to
    // nothing. In Discrepancies fetch mode the accumulated set is all we have, so the slice is
    // what makes a page.
    return fetchFilter === DISCREPANCIES_FILTER
      ? syncedItems.slice(startIndex, startIndex + limit)
      : syncedItems;
  }, [syncedItems, fetchFilter, startIndex, limit, isWalkMode]);

  const parentSyncIds = useMemo(() => pageSyncItems.map((item) => item._id), [pageSyncItems]);
  const {
    parents,
    isLoading: isScanLoading,
    error: scanError,
    mutate: retryScan,
    isValidating: isRetryingScan,
    scanParent,
  } = useSubitemDiscrepancy(currentBoard, token, parentSyncIds);

  const parentBySyncId = useMemo(() => {
    const map = new Map<string, SubitemScanParent>();
    parents.forEach((parent) => map.set(parent.parentSyncId, parent));
    return map;
  }, [parents]);

  const handleToggle = useCallback((syncId: string) => {
    setOpenParents((prev) => ({ ...prev, [syncId]: !prev[syncId] }));
  }, []);

  const [resyncRequest, setResyncRequest] = useState<SubitemResyncRequest | null>(null);

  // Kept in sync on every render (a plain assignment, not a side effect) rather than via a
  // `useEffect`, specifically so the stable handlers below can read the LATEST scan through
  // it without becoming a dependency themselves — see the comment on those handlers.
  const walkParents = useMemo(() => Array.from(walk.parents.values()), [walk.parents]);
  const parentsRef = useRef(parents);
  parentsRef.current = isWalkMode ? walkParents : parents;

  const [destructiveRequest, setDestructiveRequest] = useState<SubitemDestructiveRequest | null>(
    null,
  );

  // Task 7 wires onResyncColumn/onResyncName/onResyncSubitem below; Task 8 wires
  // onCreateMissing/onUnlink to open SubitemDestructiveModal rather than firing directly —
  // both are irreversible from inside the app, so both need the confirmation that modal builds.
  //
  // Every handler below has an empty dependency list, so its identity never changes across
  // renders — that stability is what lets `actions` (and, in turn, every ParentRow's own
  // per-parent `useMemo`) actually hold, rather than recomputing every render regardless.
  // The adapter's contract only ever hands these a subitemSyncId (and, for columns, a
  // columnKey) — never the parent, the row, or the board list a resync request needs — so
  // each looks the rest up via `parentsRef.current` (see `findSubitemContext`) instead of
  // taking `parents` as a dependency, which would defeat the stability this comment relies on.
  const handleResyncColumn = useCallback((subitemSyncId: string, columnKey: string) => {
    const context = findSubitemContext(parentsRef.current, subitemSyncId);
    const row = context?.subitem.rows.find((candidate) => candidate.columnKey === columnKey);
    if (!context || !row) return;
    setResyncRequest({
      kind: "column",
      parentSyncId: context.parentSyncId,
      subitemSyncId,
      columnKey,
      row,
      boards: context.boards,
    });
  }, []);
  const handleResyncSubitem = useCallback((subitemSyncId: string) => {
    const context = findSubitemContext(parentsRef.current, subitemSyncId);
    if (!context) return;
    setResyncRequest({
      kind: "subitem",
      parentSyncId: context.parentSyncId,
      subitemSyncId,
      subitemName: context.subitem.name,
      rows: context.subitem.rows,
      boards: context.boards,
    });
  }, []);
  const handleResyncName = useCallback((subitemSyncId: string) => {
    const context = findSubitemContext(parentsRef.current, subitemSyncId);
    const row = context?.subitem.rows.find((candidate) => candidate.columnKey === "__name__");
    if (!context || !row) return;
    setResyncRequest({
      kind: "name",
      parentSyncId: context.parentSyncId,
      subitemSyncId,
      row,
      boards: context.boards,
    });
  }, []);
  const handleCreateMissing = useCallback((subitemSyncId: string, targetBoardIds: string[]) => {
    const context = findSubitemContext(parentsRef.current, subitemSyncId);
    if (!context) return;
    setDestructiveRequest({
      kind: "create",
      parentSyncId: context.parentSyncId,
      subitemSyncId,
      subitemName: context.subitem.name,
      targetBoardIds,
      boards: context.boards,
    });
  }, []);
  // onUnlink's contract (SubitemRowActions) only ever hands back subitemSyncId — orphaned_record
  // carries no per-board evidence of which board went stale (missingOnBoards is deliberately []
  // for this state; see subitemScanAdapter.ts), so the narrowest board set this component can
  // honestly name and send is the subitem's own full linked board set, from context.boards.
  const handleUnlink = useCallback((subitemSyncId: string) => {
    const context = findSubitemContext(parentsRef.current, subitemSyncId);
    if (!context) return;
    setDestructiveRequest({
      kind: "unlink",
      parentSyncId: context.parentSyncId,
      subitemSyncId,
      subitemName: context.subitem.name,
      boardIds: context.boards.map((board) => board.boardId),
      boards: context.boards,
    });
  }, []);

  // Connect (2026-09-11): the Monday id and the board come off the subitem's `location`, which
  // the engine populates only for unlinked_subitem — the adapter only offers the action when it
  // is present, so a missing location here is a stale-grid race, not a code path to handle.
  const handleConnect = useCallback((subitemSyncId: string) => {
    const context = findSubitemContext(parentsRef.current, subitemSyncId);
    const location = context?.subitem.location;
    if (!context || !location) return;
    setDestructiveRequest({
      kind: "connect",
      parentSyncId: context.parentSyncId,
      subitemSyncId,
      subitemName: context.subitem.name,
      subitemId: location.mondayItemId,
      boardId: location.boardId,
      boardName: location.boardName,
      boards: context.boards,
    });
  }, []);

  const handleCloseResyncModal = useCallback(() => setResyncRequest(null), []);
  const handleCloseDestructiveModal = useCallback(() => setDestructiveRequest(null), []);

  const actions: SubitemRowActions = useMemo(
    () => ({
      onResyncColumn: handleResyncColumn,
      onResyncSubitem: handleResyncSubitem,
      onResyncName: handleResyncName,
      onCreateMissing: handleCreateMissing,
      onUnlink: handleUnlink,
      onConnect: handleConnect,
    }),
    [
      handleResyncColumn,
      handleResyncSubitem,
      handleResyncName,
      handleCreateMissing,
      handleUnlink,
      handleConnect,
    ],
  );

  // Same normalisation SharedColumns.tsx uses: an absent/empty filter has always meant
  // "discrepancies", never "all".
  const gridFilter: "all" | "discrepancies" =
    filterQuery === DISCREPANCIES_FILTER || !filterQuery ? "discrepancies" : "all";

  /*
   * The rows this page shows.
   *
   * Discrepancies mode pages the WALK's drifting set — every parent on the board whose scan
   * proved subitem drift, in the board's order — so page N is always the Nth `limit` drifting
   * parents, whichever windows they were found in. This used to filter INSIDE the page window
   * instead: take 25 parents, scan them, hide the clean ones. Each page was then "whichever of
   * these 25 happen to drift" — three rows, then none, then one — while the page control was
   * sized from the unfiltered set, so paging through it looked exactly like a broken app. That
   * was a compromise forced by the scan being bounded to a page; the walk is what removes the
   * need for it.
   *
   * All mode is unchanged: the page it was handed, every parent shown, verdicts filling in as
   * the page scan lands.
   */
  const walkPageRows = useMemo(
    () =>
      walk.matching.slice(startIndex, startIndex + limit).map((parent) => ({
        _id: parent.parentSyncId,
        // The scan's live Monday name. There is no page list to prefer in this mode — the one
        // case ParentRow's own note on names describes.
        item_name: parent.parentName,
      })),
    [walk.matching, startIndex, limit],
  );
  const pageItems = isWalkMode ? walkPageRows : pageSyncItems;
  const parentFor = (syncId: string) =>
    isWalkMode ? walk.parents.get(syncId) : parentBySyncId.get(syncId);

  // Checked BEFORE the empty-state gate below, and short-circuits the whole list rather than
  // rendering alongside it — same shape as Resynchronization.tsx's own `error` handling
  // (Resynchronization.tsx:190-198), which replaces ItemsTable entirely rather than trying to
  // render partial results. Also why an already-expanded row can never be stuck showing
  // "Loading..." forever on an error: there is no row tree left mounted to be stuck in it.
  if (isWalkMode ? walk.status === "error" : scanError) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "var(--spacing-medium)",
          padding: "var(--spacing-large)",
        }}
      >
        <Text align="center" element="p" type="text1">
          An error occurred while scanning subitems. Please try again.
        </Text>
        <Button
          onClick={() => (isWalkMode ? walk.retry() : retryScan())}
          color="primary"
          loading={!isWalkMode && isRetryingScan}
        >
          Retry
        </Button>
      </div>
    );
  }

  // The walk has asked about every parent and none drifts. Named, with the count, because
  // "No items to display." after a filter is exactly what the old per-page filtering showed on
  // a page that happened to be clean — and the user read it as the app being broken.
  if (isWalkMode && walk.status === "done" && walk.matching.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "var(--spacing-small)",
          textAlign: "center",
          padding: "var(--spacing-large)",
        }}
      >
        <Text type="text2" color="secondary">
          {`No subitem discrepancies found across ${countOf(walk.totalCount, "item")}.`}
        </Text>
        {walk.skippedCount > 0 ? <SkippedNote count={walk.skippedCount} /> : null}
      </div>
    );
  }

  // `itemsLoading` reflects only whether `syncedItems` itself loaded — it says nothing about
  // whether the subitem scan has. Without `isScanLoading` here too, this would render a confident
  // "No items to display." for the entire window the scan is in flight. Mirrors ItemsTable's own
  // `shouldShowEmptyMessage`, which likewise gates on its in-flight flag (`isProcessing`), not
  // only on `itemsLoading` (ItemsTable.tsx:116-119).
  if (!isWalkMode && !itemsLoading && !isScanLoading && pageItems.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "var(--spacing-large)" }}>
        <Text type="text2" color="secondary">
          No items to display.
        </Text>
      </div>
    );
  }

  // In Discrepancies mode the skeleton stands in until the FIRST drifting parent is known — the
  // walk keeps going behind the rows once there is something to show, and the progress line
  // above the list says so. `idle` is treated as loading too: it only reaches here from a walk
  // that has not been enabled yet, which is a page-wiring bug, and a skeleton is the honest
  // render for "nothing known".
  const isListLoading = isWalkMode
    ? walk.status !== "done" && walkPageRows.length === 0
    : itemsLoading || isScanLoading;

  return (
    <div>
      {isWalkMode ? <WalkProgress walk={walk} /> : null}
      {/* One bordered container around the whole set, with neighbours divided by a single
          hairline each — rather than a bordered card per parent separated by gutters. The
          divider lives on the wrapper's children so exactly one element owns each line;
          a border on both sides of a gap is how a list ends up with doubled rules. */}
      <div
        style={{
          border: "1px solid var(--layout-border-color)",
          borderRadius: "var(--border-radius-medium)",
          overflow: "hidden",
          background: "var(--primary-background-color)",
        }}
      >
        {/*
          The loading state, and the reason this list has one at all: with neither the item
          list nor the scan resolved, `pageItems` is empty and the empty-state gate above
          deliberately does not fire — so this rendered a bare bordered box, which looks
          exactly like a board with nothing on it. The Items tab has shown skeleton rows here
          since Phase 1; both now draw them from the same component.
        */}
        {isListLoading ? (
          <DiscrepancyListSkeleton rows={limit} />
        ) : (
          pageItems.map((syncItem, index) => (
            <div
              key={syncItem._id}
              style={{
                borderTop: index === 0 ? "none" : "1px solid var(--layout-border-color)",
              }}
            >
              <ParentRow
                syncItem={syncItem}
                parent={parentFor(syncItem._id)}
                isOpen={Boolean(openParents[syncItem._id])}
                onToggle={() => handleToggle(syncItem._id)}
                gridFilter={gridFilter}
                actions={actions}
                onScanParent={isWalkMode ? walk.rescanParent : scanParent}
              />
            </div>
          ))
        )}
      </div>
      <SubitemResyncModal
        request={resyncRequest}
        token={token}
        onClose={handleCloseResyncModal}
        // Patches the cached scan from the engine's own result FIRST, then revalidates —
        // `mutate(updater, { revalidate: true })` commits the patched page and starts the
        // refetch behind it. The refetch is the same full-page scan `retryScan` always ran
        // (Phase 3a invalidates the server-side flags cache on a successful write, so it
        // returns the truth); the patch is what the user sees during the tens of seconds
        // it takes. Until 2026-09-10 this was a bare `mutate()`, and the row kept showing
        // the drift the user had just fixed until that scan came back. The 2026-09-28
        // staging→dev merge dropped the patch again; do not reduce this to `retryScan()`.
        //
        // In Discrepancies mode the touched parent is rescanned on its own and merged into
        // the walk — a full restart would re-walk the board to learn about one parent — and
        // once its fresh verdict is clean it leaves the drifting set, exactly as a resynced
        // row leaves the Items tab's.
        onResynced={(request, result) => {
          if (isWalkMode) {
            void walk.rescanParent(request.parentSyncId).catch(swallow);
            return;
          }
          retryScan((current) => patchSubitemScanAfterResync(current, request, result), {
            revalidate: true,
          });
        }}
      />
      <SubitemDestructiveModal
        request={destructiveRequest}
        token={token}
        onClose={handleCloseDestructiveModal}
        // Same revalidation pattern as SubitemResyncModal above: `retryScan` is the shared
        // `mutate` for the one scan key every parent uses, refetched rather than patched
        // optimistically — critical here, since an optimistic row removal after an unlink
        // whose deletedCount was 0 would show a deletion that never happened.
        onResynced={() => {
          if (isWalkMode) {
            if (destructiveRequest) {
              void walk.rescanParent(destructiveRequest.parentSyncId).catch(swallow);
            }
            return;
          }
          retryScan();
        }}
      />
    </div>
  );
};

export default SubitemsTable;
